import React, { Fragment, useEffect, useMemo } from 'react';
import { resizeImage } from 'src/common/resizeImage';

interface Properties {
    imageUrls?: string[];
    name: string;
    formik: { setFieldValue: (path: string, val: any) => void };
    multiple?: boolean;
    required?: boolean;
    images: string[];
    onImagesChange: (images: string[]) => void;
    value: string | string[] | undefined;
}

function ImageSelector(props: Properties) {
    const { imageUrls, name, formik, multiple = false, required = false, images, onImagesChange, value } = props;

    // Selected images normalized for UI
    const selectedImages: string[] = useMemo(() => {
        if (multiple) return Array.isArray(value) ? value : [];
        return typeof value === 'string' && value ? [value] : [];
    }, [value, multiple]);

    // Download remote URLs once (only when we have URLs and no local images yet)
    useEffect(() => {
        if (!imageUrls?.length || images.length > 0) return;
        (async () => {
            const valid = imageUrls.filter((u): u is string => typeof u === 'string' && u.trim().length > 0);
            if (!valid.length) return;
            const newImages = (await Promise.all(valid.map(fetchToDataUrl))).filter(
                (img): img is string => img !== null,
            );
            if (newImages.length) onImagesChange([...images, ...newImages]);

            // keep only base64 in form value; write only if changed
            const nextSelected: string | string[] = multiple
                ? Array.isArray(value)
                    ? value.filter((u: string) => typeof u === 'string' && !u.startsWith('http'))
                    : []
                : typeof value === 'string' && !value.startsWith('http')
                  ? value
                  : '';

            if (
                multiple
                    ? !arrayShallowEqual(nextSelected as string[], (value as string[]) || [])
                    : nextSelected !== (value || '')
            ) {
                formik.setFieldValue(name, nextSelected);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrls]); // guarded by images.length === 0

    // If required and nothing selected, auto-select the first available image (guarded)
    useEffect(() => {
        if (!required || images.length === 0) return;
        if (selectedImages.length === 0) {
            const first = images[0];
            const nextVal = multiple ? [first] : first;
            const already = multiple
                ? Array.isArray(value) && value.length === 1 && value[0] === first
                : value === first;
            if (!already) formik.setFieldValue(name, nextVal);
        }
    }, [required, images, multiple, name, formik, selectedImages.length, value]);

    async function fetchToDataUrl(url: string): Promise<string | null> {
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
            const blob = await res.blob();
            const file = new File([blob], url.substring(url.lastIndexOf('/') + 1) || 'image', {
                type: blob.type || 'image/png',
            });
            return await loadFile(file);
        } catch (error) {
            console.warn('Failed to fetch image for ImageSelector', url, error);
            return null;
        }
    }

    function handleImageClick(image: string) {
        if (multiple) {
            const isSelected = selectedImages.includes(image);
            const next = isSelected ? selectedImages.filter((u) => u !== image) : [...selectedImages, image];
            if (!arrayShallowEqual(next, selectedImages)) formik.setFieldValue(name, next);
        } else {
            const next = selectedImages[0] === image ? '' : image;
            if (next !== (value || '')) formik.setFieldValue(name, next);
        }
    }

    async function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
        event.preventDefault();
        const files = event.target.files;
        if (files && files.length) {
            const newImages = await Promise.all(Array.from(files).map(loadFile));
            const unique = newImages.filter((img) => !images.includes(img));
            if (unique.length) onImagesChange([...images, ...unique]);
            event.target.value = '';
        }
    }

    function loadFile(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async () => {
                if (reader.readyState === reader.DONE) {
                    const resized = await resizeImage(reader.result as string, 800, 0.8);
                    resolve(resized); // it's a data URL; no revokeObjectURL
                }
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    return (
        <Fragment>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 10 }}>
                {images.map((url, index) => (
                    <div
                        key={url + index}
                        style={{
                            border: selectedImages.includes(url) ? '4px solid blue' : '4px solid transparent',
                            cursor: 'pointer',
                            position: 'relative',
                            height: 208,
                            width: 308,
                        }}
                        onClick={() => handleImageClick(url)}
                    >
                        <img
                            src={url}
                            alt={`img-${index}`}
                            style={{
                                maxWidth: 300,
                                maxHeight: 200,
                                top: '50%',
                                left: 0,
                                right: 0,
                                margin: '0 auto',
                                transform: 'translateY(-50%)',
                                position: 'absolute',
                            }}
                        />
                    </div>
                ))}
            </div>

            <input type="file" multiple onChange={handleFileSelected} />
        </Fragment>
    );
}

export default React.memo(ImageSelector, (prev, next) => {
    return (
        prev.name === next.name &&
        prev.multiple === next.multiple &&
        prev.required === next.required &&
        prev.value === next.value && // for primitives or stable array reference if Formik preserves it
        prev.images.length === next.images.length &&
        prev.images[prev.images.length - 1] === next.images[next.images.length - 1]
    );
});

/** utils */
function arrayShallowEqual(a: any[], b: any[]): boolean {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
